import { useMemo } from 'react';
import { useMeta } from '../hooks/useWorkQueue';
import type { StatusMeta } from './workQueue';

export interface StatusCatalog {
    /** '9.1. Waiting for post-approval redesign' - code + label. */
    display(id: string): string;
    /** 'Waiting for post-approval redesign' - label only. */
    label(id: string): string;
    code(id: string): string | undefined;
    meta(id: string): StatusMeta | undefined;
}

// Historical timeline payloads can carry retired/pre-rename ids the catalog
// has never heard of - fall back to stripping the macro prefix off the id
// itself so nothing ever renders blank.
const MACRO_PREFIX = /^[A-Za-z]+(?:\s[A-Za-z]+)? - /;

function fallbackLabel(id: string): string {
    return id.replace(MACRO_PREFIX, '');
}

export function makeStatusCatalog(statuses: StatusMeta[] | undefined): StatusCatalog {
    const byId = new Map((statuses ?? []).map((s) => [s.id, s]));
    return {
        display(id) {
            const meta = byId.get(id);
            return meta ? `${meta.code}. ${meta.label}` : fallbackLabel(id);
        },
        label(id) {
            return byId.get(id)?.label ?? fallbackLabel(id);
        },
        code(id) {
            return byId.get(id)?.code;
        },
        meta(id) {
            return byId.get(id);
        }
    };
}

export function useStatusCatalog(): StatusCatalog {
    const { data } = useMeta();
    return useMemo(() => makeStatusCatalog(data?.statuses), [data?.statuses]);
}
